// src/pages/Cajas.js
//
// Administración de cajas físicas + métodos de pago.
//
// - Tabla de cajas por sucursal con estado, fondo inicial, apertura activa.
// - Botón "Nueva caja" → dialog (codigo, nombre, sucursal, almacén, fondo).
// - Sección "Métodos de pago" con tabla editable + botón inicializar defaults.
//
// Consume el heredado /api/pos/caja/*.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Typography, Stack, Button, IconButton, Chip, Alert, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TextField, MenuItem, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, InputAdornment, Switch, Tabs, Tab, Divider, Grid,
} from '@mui/material';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import AddIcon         from '@mui/icons-material/Add';
import EditIcon        from '@mui/icons-material/Edit';
import PaymentIcon     from '@mui/icons-material/Payment';
import BoltIcon        from '@mui/icons-material/Bolt';
import RefreshIcon     from '@mui/icons-material/Refresh';
import { useApp } from '../context/AppContext';
import { caja as apiCaja, sucursales as apiSucursales, almacenes as apiAlmacenes } from '../api/posApi';

const fmtCOP = (n) => (Number(n) || 0).toLocaleString('es-CO',
  { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

const TabPanel = ({ children, value, index }) => (
  <Box hidden={value !== index} sx={{ pt: 3 }}>{value === index && children}</Box>
);

const Cajas = () => {
  const { empresa, sucursalActivaId, notify } = useApp();
  const empresaId = empresa?.empresaId || empresa?.EmpresaId;

  const [tab, setTab] = useState(0);
  const [cajas, setCajas] = useState([]);
  const [metodos, setMetodos] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [dialog, setDialog] = useState(null);

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setCargando(true);
    try {
      const [c, m, s] = await Promise.all([
        apiCaja.listarCajas(empresaId, sucursalActivaId),
        apiCaja.listarMetodosPago(empresaId),
        apiSucursales.listar(empresaId),
      ]);
      setCajas(Array.isArray(c) ? c : []);
      setMetodos(Array.isArray(m) ? m : []);
      setSucursales(Array.isArray(s) ? s : []);
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudo cargar.', 'error');
    } finally { setCargando(false); }
  }, [empresaId, sucursalActivaId, notify]);

  useEffect(() => { cargar(); }, [cargar]);

  const inicializarMetodos = async () => {
    try {
      const r = await apiCaja.inicializarMetodosPago(empresaId);
      notify(r.message || 'Métodos inicializados.', 'success');
      await cargar();
    } catch (e) { notify(e.mensajeUsuario || 'No se pudo inicializar.', 'error'); }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <PointOfSaleIcon color="primary" fontSize="large" />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight={700}>Cajas</Typography>
          <Typography variant="body2" color="text.secondary">
            Administración de cajas físicas y métodos de pago
          </Typography>
        </Box>
        <IconButton onClick={cargar}><RefreshIcon /></IconButton>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab label="Cajas físicas" />
        <Tab label="Métodos de pago" />
      </Tabs>

      {/* ─── CAJAS ─── */}
      <TabPanel value={tab} index={0}>
        <Card>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Cajas registradas</Typography>
            <Button variant="contained" startIcon={<AddIcon />}
                    onClick={() => setDialog({ tipo: 'nueva-caja' })}>
              Nueva caja
            </Button>
          </Box>
          {cargando ? (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : cajas.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>
              Aún no tienes cajas registradas. Crea al menos una para poder operar el POS.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Sucursal</TableCell>
                    <TableCell align="right">Fondo inicial</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Turno activo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cajas.map(c => (
                    <TableRow key={c.cajaId} hover>
                      <TableCell><Typography variant="body2" fontWeight={500}>{c.codigo}</Typography></TableCell>
                      <TableCell>{c.nombre}</TableCell>
                      <TableCell><Typography variant="caption">{c.sucursalNombre}</Typography></TableCell>
                      <TableCell align="right">{fmtCOP(c.fondoInicial)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={c.estado}
                              color={c.estado === 'ABIERTA' ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell>
                        {c.aperturaActiva ? (
                          <Box>
                            <Typography variant="caption" display="block">
                              {c.aperturaActiva.nombreUsuario}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Inicial {fmtCOP(c.aperturaActiva.montoInicial)}
                            </Typography>
                          </Box>
                        ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </TabPanel>

      {/* ─── MÉTODOS DE PAGO ─── */}
      <TabPanel value={tab} index={1}>
        <Card>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Métodos de pago aceptados</Typography>
            {metodos.length === 0 && (
              <Button variant="outlined" startIcon={<BoltIcon />} onClick={inicializarMetodos} sx={{ mr: 1 }}>
                Inicializar defaults
              </Button>
            )}
            <Button variant="contained" startIcon={<AddIcon />}
                    onClick={() => setDialog({ tipo: 'nuevo-metodo' })}>
              Nuevo método
            </Button>
          </Box>
          {cargando ? (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : metodos.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>
              No hay métodos de pago configurados. Pulsa <b>Inicializar defaults</b> para crear
              Efectivo, Tarjeta débito, Tarjeta crédito, Transferencia y Nota crédito.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Orden</TableCell>
                    <TableCell>Código</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell align="center">Requiere ref.</TableCell>
                    <TableCell align="center">Permite vuelto</TableCell>
                    <TableCell align="right">Comisión %</TableCell>
                    <TableCell align="center">Activo</TableCell>
                    <TableCell align="right"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {metodos.map(m => (
                    <TableRow key={m.metodoPagoId} hover>
                      <TableCell>{m.orden}</TableCell>
                      <TableCell><Typography variant="body2" fontWeight={500}>{m.codigo}</Typography></TableCell>
                      <TableCell>{m.nombre}</TableCell>
                      <TableCell><Chip size="small" label={m.tipoPago} variant="outlined" /></TableCell>
                      <TableCell align="center">{m.requiereReferencia ? '✓' : '—'}</TableCell>
                      <TableCell align="center">{m.permiteVuelto ? '✓' : '—'}</TableCell>
                      <TableCell align="right">{m.comisionPorcentaje > 0 ? `${m.comisionPorcentaje}%` : '—'}</TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={m.activo ? 'Sí' : 'No'}
                              color={m.activo ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => setDialog({ tipo: 'editar-metodo', metodo: m })}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </TabPanel>

      {dialog?.tipo === 'nueva-caja' && (
        <DialogNuevaCaja empresaId={empresaId} sucursales={sucursales}
                        sucursalActivaId={sucursalActivaId}
                        onClose={() => setDialog(null)}
                        onDone={async () => { setDialog(null); await cargar(); notify('Caja creada.', 'success'); }}
                        onError={(m) => notify(m, 'error')} />
      )}
      {(dialog?.tipo === 'nuevo-metodo' || dialog?.tipo === 'editar-metodo') && (
        <DialogMetodoPago empresaId={empresaId} metodo={dialog.metodo}
                          onClose={() => setDialog(null)}
                          onDone={async () => { setDialog(null); await cargar(); notify('Método guardado.', 'success'); }}
                          onError={(m) => notify(m, 'error')} />
      )}
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════
// Nueva caja
// ══════════════════════════════════════════════════════════════════════

function DialogNuevaCaja({ empresaId, sucursales, sucursalActivaId, onClose, onDone, onError }) {
  const [f, setF] = useState({
    codigo: '', nombre: '',
    sucursalId: sucursalActivaId || (sucursales[0]?.sucursalId || ''),
    almacenId: '', fondoInicial: 0,
  });
  const [almacenes, setAlmacenes] = useState([]);
  const [g, setG] = useState(false);

  useEffect(() => {
    if (!f.sucursalId) return;
    (async () => {
      try {
        const a = await apiAlmacenes.listar(f.sucursalId);
        setAlmacenes(Array.isArray(a) ? a : []);
        // Auto-elegir principal si existe
        const principal = a?.find?.(x => x.esAlmacenPrincipal);
        if (principal) setF(s => ({ ...s, almacenId: principal.almacenId }));
      } catch { /* silent */ }
    })();
  }, [empresaId, f.sucursalId]);

  const submit = async () => {
    setG(true);
    try {
      await apiCaja.crearCaja({
        empresaId, sucursalId: Number(f.sucursalId),
        almacenId: f.almacenId ? Number(f.almacenId) : null,
        codigo: f.codigo.trim(), nombre: f.nombre.trim(),
        fondoInicial: Number(f.fondoInicial) || 0,
      });
      onDone();
    } catch (e) { onError(e.mensajeUsuario || 'No se pudo crear.'); }
    finally { setG(false); }
  };

  const ok = f.codigo.trim() && f.nombre.trim() && f.sucursalId;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Nueva caja</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField required label="Código" placeholder="C01" sx={{ width: 140 }}
                       value={f.codigo} onChange={(e) => setF(s => ({ ...s, codigo: e.target.value.toUpperCase() }))} />
            <TextField required fullWidth label="Nombre" placeholder="Caja principal"
                       value={f.nombre} onChange={(e) => setF(s => ({ ...s, nombre: e.target.value }))} />
          </Stack>
          <TextField required select label="Sucursal"
                     value={f.sucursalId} onChange={(e) => setF(s => ({ ...s, sucursalId: e.target.value, almacenId: '' }))}>
            {sucursales.map(s => (
              <MenuItem key={s.sucursalId} value={s.sucursalId}>{s.nombre}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Almacén asociado (opcional)"
                     value={f.almacenId} onChange={(e) => setF(s => ({ ...s, almacenId: e.target.value }))}
                     helperText="El almacén desde el que esta caja descontará stock al vender.">
            <MenuItem value="">(usar el principal automáticamente)</MenuItem>
            {almacenes.map(a => (
              <MenuItem key={a.almacenId} value={a.almacenId}>
                {a.nombre}{a.esAlmacenPrincipal ? ' (principal)' : ''}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Fondo inicial estándar" type="number"
                     value={f.fondoInicial} onChange={(e) => setF(s => ({ ...s, fondoInicial: e.target.value }))}
                     InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                     helperText="Sugerencia para el monto inicial al abrir turno. Puedes ajustarlo al abrir." />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={g}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={!ok || g}>
          {g ? 'Creando…' : 'Crear caja'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Método de pago
// ══════════════════════════════════════════════════════════════════════

function DialogMetodoPago({ empresaId, metodo, onClose, onDone, onError }) {
  const editando = !!metodo;
  const [f, setF] = useState({
    codigo: metodo?.codigo || '',
    nombre: metodo?.nombre || '',
    tipoPago: metodo?.tipoPago || 'EFECTIVO',
    requiereReferencia: metodo?.requiereReferencia ?? false,
    permiteVuelto: metodo?.permiteVuelto ?? false,
    comisionPorcentaje: metodo?.comisionPorcentaje ?? 0,
    orden: metodo?.orden ?? 10,
    icono: metodo?.icono || '',
    color: metodo?.color || '',
    activo: metodo?.activo ?? true,
  });
  const [g, setG] = useState(false);
  const submit = async () => {
    setG(true);
    try {
      if (editando) {
        await apiCaja.actualizarMetodoPago(metodo.metodoPagoId, {
          metodoPagoId: metodo.metodoPagoId, empresaId,
          codigo: f.codigo, nombre: f.nombre, tipoPago: f.tipoPago,
          requiereReferencia: f.requiereReferencia, permiteVuelto: f.permiteVuelto,
          comisionPorcentaje: Number(f.comisionPorcentaje) || 0,
          comisionFija: 0,
          icono: f.icono || null, color: f.color || null,
          orden: Number(f.orden) || 10, activo: f.activo,
        });
      } else {
        await apiCaja.crearMetodoPago({
          empresaId, codigo: f.codigo, nombre: f.nombre, tipoPago: f.tipoPago,
          requiereReferencia: f.requiereReferencia, permiteVuelto: f.permiteVuelto,
          comisionPorcentaje: Number(f.comisionPorcentaje) || 0,
          icono: f.icono || null, color: f.color || null,
          orden: Number(f.orden) || 10, activo: true,
        });
      }
      onDone();
    } catch (e) { onError(e.mensajeUsuario || 'No se pudo guardar.'); }
    finally { setG(false); }
  };
  const ok = f.codigo.trim() && f.nombre.trim();
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{editando ? 'Editar método' : 'Nuevo método de pago'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField required label="Código" sx={{ width: 140 }}
                       value={f.codigo} onChange={(e) => setF(s => ({ ...s, codigo: e.target.value.toUpperCase() }))} />
            <TextField required fullWidth label="Nombre"
                       value={f.nombre} onChange={(e) => setF(s => ({ ...s, nombre: e.target.value }))} />
          </Stack>
          <TextField select label="Tipo de pago"
                     value={f.tipoPago} onChange={(e) => setF(s => ({ ...s, tipoPago: e.target.value }))}>
            <MenuItem value="EFECTIVO">Efectivo</MenuItem>
            <MenuItem value="TARJETA_DEBITO">Tarjeta débito</MenuItem>
            <MenuItem value="TARJETA_CREDITO">Tarjeta crédito</MenuItem>
            <MenuItem value="TRANSFERENCIA">Transferencia</MenuItem>
            <MenuItem value="CREDITO">Crédito / Fiado</MenuItem>
            <MenuItem value="NOTA_CREDITO">Nota de crédito</MenuItem>
            <MenuItem value="OTRO">Otro</MenuItem>
          </TextField>
          <Stack direction="row" spacing={2}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <Switch checked={f.requiereReferencia}
                      onChange={(e) => setF(s => ({ ...s, requiereReferencia: e.target.checked }))} />
              Requiere referencia
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <Switch checked={f.permiteVuelto}
                      onChange={(e) => setF(s => ({ ...s, permiteVuelto: e.target.checked }))} />
              Permite vuelto
            </label>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Comisión %" type="number" sx={{ width: 160 }}
                       value={f.comisionPorcentaje}
                       onChange={(e) => setF(s => ({ ...s, comisionPorcentaje: e.target.value }))}
                       InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
            <TextField label="Orden" type="number" sx={{ width: 100 }}
                       value={f.orden} onChange={(e) => setF(s => ({ ...s, orden: e.target.value }))} />
            {editando && (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <Switch checked={f.activo} onChange={(e) => setF(s => ({ ...s, activo: e.target.checked }))} />
                Activo
              </label>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={g}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={!ok || g}>
          {g ? 'Guardando…' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default Cajas;
